import "./SubcontractingCompany.scss";
import InfoContext from "pages/InfoContext";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { getFormattedAuxilaryCode } from "utils/functions";
import { useContext, useEffect } from "react";
import { POPUP_STATUS, SIREN_SEARCH_ENTITY } from "constants/constants";
import {
  SUBCONTRACTING_COMPANY_FORM_LABELS,
  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS,
} from "constants/forms";
import {
  renderAuxiliaryCode,
  renderCompanyName,
  renderInputAddress,
  renderInputCity,
  renderInputCountry,
  renderInputPostalCode,
  renderSiren,
  renderSiret,
} from "../CommonInputs";
import { getCellErrorHelper } from "utils/helper";
import PropTypes from "prop-types";

const SubcontractingCompanyInfoForm = ({
  control,
  errors,
  readOnly,
  register,
  setValue,
  watch,
  cellHighlight,
}) => {
  const {
    searchDavidsonBySiren,
    sirenData,
    lang,
    dataCountry,
    sirenErrorMessage,
    setSirenErrorMessage,
    setStatus,
    setCode,
    code,
  } = useContext(InfoContext);

  const {
    company_name,
    postal_code,
    siret,
    city,
    address,
    country,
    siren: sirenValue,
  } = sirenData || {};

  const siren = watch("siren", "");

  useEffect(() => {
    const fieldsMap = {
      company_name: company_name,
      postal_code: postal_code,
      siret: siret,
      city: city,
      address: address,
      country: country,
      auxiliary_code: sirenValue
        ? getFormattedAuxilaryCode(sirenValue, false)
        : "",
    };
    Object.entries(fieldsMap).forEach(([field, value]) =>
      setValue(field, !siren ? "" : value)
    );
  }, [sirenValue]);

  useEffect(() => {
    setSirenErrorMessage("");
  }, [siren]);

  useEffect(() => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(sirenErrorMessage);
  }, [sirenErrorMessage]);

  return (
    <div className="subcontracting-company-info">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="company" defaultMessage="Société" />}
      />
      <div className="subcontracting-company-info-form">
        <div className="block">
          <div className="line-1">
            <div
              className={`cell-padding ${
                !watch("company_name") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderCompanyName({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.company_name,
                placeholder:
                  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.company_name,
                readOnly,
                register,
              })}
            </div>
            <div
              className={`cell-padding ${
                (!watch("siren") && cellHighlight && "errorCellColor") ||
                (errors?.siren && "errorCellColor")
              }`}
            >
              {renderSiren({
                errorMessage: sirenErrorMessage || errors?.siren?.message,
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.siren,
                placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.siren,
                readOnly,
                register,
                value: watch("siren"),
                nationalityId: watch("nationality")?.[0]?.value,
                onClickSearch: (siren) =>
                  searchDavidsonBySiren(
                    siren,
                    SIREN_SEARCH_ENTITY.SUBCONTRACTING_COMPANY
                  ),
              })}
            </div>
            <div
              className={`cell-padding ${getCellErrorHelper({
                hasValue: watch("siret"),
                hasError: errors?.siret,
                cellHighlight,
              })}`}
            >
              {renderSiret({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.siret,
                placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.siret,
                readOnly,
                register,
                errorMessage: errors?.siret?.message,
              })}
            </div>
          </div>
          <div className="line-2">
            <div
              className={`cell-padding ${
                !watch("auxiliary_code") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderAuxiliaryCode({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.auxiliary_code,
                placeholder:
                  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.auxiliary_code,
                readOnly,
                register,
              })}
            </div>
            <div
              className={`cell-padding ${
                !watch("country") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputCountry({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.country,
                placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.country,
                readOnly,
                register,
                countries: dataCountry,
                value: watch("country"),
                setValue,
                lang,
              })}
            </div>
            <div
              className={`cell-padding ${
                !watch("city") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputCity({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.city,
                placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.city,
                readOnly,
                register,
              })}
            </div>
            <div
              className={`cell-padding ${
                !watch("postal_code") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputPostalCode({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.postal_code,
                placeholder:
                  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.postal_code,
                readOnly,
                register,
              })}
            </div>
          </div>
        </div>
        <div
          className={`address-block ${
            !watch("adress") && cellHighlight && "errorCellColor"
          }`}
        >
          {renderInputAddress({
            control,
            errors,
            label: SUBCONTRACTING_COMPANY_FORM_LABELS.address,
            placeholder: SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.address,
            readOnly,
            register,
            id: SUBCONTRACTING_COMPANY_FORM_LABELS.address.id,
          })}
        </div>
      </div>
    </div>
  );
};

export default SubcontractingCompanyInfoForm;

SubcontractingCompanyInfoForm.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  register: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  cellHighlight: PropTypes.bool,
};
